import { useState } from 'react';
import { CaretLeftIcon, GearIcon } from '@radix-ui/react-icons';
import { Avatar, Badge, Button, Skeleton, TabNav } from '@radix-ui/themes';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import type { FitPlayerProfileResponse, GetFitPlayerProfilesByIdUserInfoResponse } from 'api/generated';
import {
  isCurrentUser,
  isCurrentUserAdmin,
  isUserPremiumClubMember,
  isUserPremiumMember,
} from 'helpers/authentication';
import { pushEvent } from 'helpers/crmUtils';
import { useAppNavigation } from 'helpers/useAppNavigation';
import { useWindowSize } from 'helpers/useWindowSize';
import Analytics from 'ui/design-base/Analytics';
import ActivePopover, { ActivePopoverColor } from './ActivePopover';
import AgeCategoryLabel from './AgeCategoryLabel';
import FollowUnfollowButton from './FollowUnfollowButton';
import InviteDialog from './InviteDialog';
import MembershipLabel from './MembershipLabel';
import MessageDialog from './MessageDialog';
import PlayerObjectSkeleton from './PlayerObjectSkeleton';
import RankLabel from './RankLabel';
import { ShareHeaderButton } from './ShareHeaderButton';
import { ShowWrappedDialog } from './ShowWrappedDialog';
import VisitNumber from './VisitNumber';

export const PlayerHeader: React.FC<
  Partial<FitPlayerProfileResponse> & {
    user?: GetFitPlayerProfilesByIdUserInfoResponse;
    loading?: boolean;
    isCurrentUser?: boolean;
    hideMenu?: boolean;
    photo_url?: string;
  }
> = ({
  id,
  name,
  active,
  thumbnail,
  photo_url,
  premium,
  premium_expiration,
  category,
  rank,
  user_id,
  fit_player_profile_id,
  followed,
  followable_type,
  views,
  user,
  enable_wrapped,
  shop_profile_id,
  loading = false,
  hideMenu = false,
}) => {
  const size = useWindowSize();
  const { ref, inView } = useInView({ threshold: size.width > 768 ? 1 : 0.4 });
  const subMenuSticky = size.height > 600;
  const { navigateBack, navigate } = useAppNavigation();
  const isUser = user_id && user_id !== fit_player_profile_id;
  const [isWrappedDialogOpen, setIsWrappedDialogOpen] = useState(false);

  const activeTab = () => {
    if (window.location.pathname.includes('partite')) return 1;
    else if (window.location.pathname.includes('statistiche')) return 2;
    else if (window.location.pathname.includes('social')) return 3;
    else if (window.location.pathname.includes('shop')) return 4;
    else return 0;
  };

  const photo = photo_url ? photo_url : thumbnail ? thumbnail : '';

  const urlPath = isCurrentUser(user_id!) ? '/profilo' : `/giocatore/${id}`;

  const subMenu = (
    <div
      id='submenu'
      className={classNames(
        { 'sticky z-9 md:top-[calc(11rem-1px)]': subMenuSticky },
        { 'top-[calc(10rem+4px)]': subMenuSticky && Capacitor.isNativePlatform() },
        { 'top-32': subMenuSticky && !Capacitor.isNativePlatform() },
      )}
    >
      <TabNav.Root className='bg-neutral-neutral-2 flex justify-center rounded-b-xl md:gap-4'>
        <TabNav.Link asChild active={activeTab() === 0}>
          <Link to={id ? urlPath : '#'}>Panoramica</Link>
        </TabNav.Link>
        <TabNav.Link asChild active={activeTab() === 1}>
          <Link className='px-0 md:px-2' to={id ? `${urlPath}/partite` : '#'}>
            Partite
          </Link>
        </TabNav.Link>
        <TabNav.Link asChild active={activeTab() === 2}>
          <Link className='px-0 md:px-2' to={id ? `${urlPath}/statistiche` : '#'}>
            Statistiche
          </Link>
        </TabNav.Link>
        <TabNav.Link asChild active={activeTab() === 3}>
          <Link className='px-0 md:px-2' to={id ? `${urlPath}/social` : '#'}>
            Social
          </Link>
        </TabNav.Link>
        {shop_profile_id && (
          <TabNav.Link asChild active={activeTab() === 4}>
            <Link className='px-0 md:px-2' to={id ? `/shop/${shop_profile_id}` : '#'}>
              Shop
            </Link>
          </TabNav.Link>
        )}
      </TabNav.Root>
    </div>
  );

  const buttons = (
    <div id='buttons' className='flex items-center justify-center gap-2 self-stretch p-2 md:items-end md:justify-end'>
      <div id='segui'>
        <FollowUnfollowButton id={id!} type={followable_type!} followed={followed!} />
      </div>
      <div id='message'>
        {isUser ? (
          <MessageDialog name={name!} id={id!} thumbnail={photo} recipient_type='fit_player_profile' />
        ) : (
          <InviteDialog currentUser={false} />
        )}
      </div>
      <ShareHeaderButton url={urlPath} name={name || 'Giocatore'} type='giocatore' />
      {/* DISABLED FOR NOW - 3 DOT MENU ON PLAYER
      <div>
        <IconButton variant='outline' className='hover:cursor-pointer'>
          <DotsVerticalIcon width='18' height='18' />
        </IconButton>
      </div> */}
    </div>
  );

  const showWrapped = () => {
    if (isCurrentUserAdmin()) return true;
    if (isCurrentUser(user_id!) && enable_wrapped) {
      return true;
    }
    return false;
  };

  const currentUserButtons = (
    <div id='buttons' className='flex items-center justify-center gap-2 self-stretch p-2 lg:items-end lg:justify-end'>
      <div className='flex flex-col gap-4 md:flex-row md:gap-2'>
        {showWrapped() && (
          <div id='wrapped' className='flex w-full justify-center'>
            <Button
              variant='solid'
              className='from-accent-9 to-accent-11 min-w-full bg-linear-to-r whitespace-nowrap hover:cursor-pointer md:min-w-28'
              onClick={() => {
                if (Capacitor.isNativePlatform()) {
                  pushEvent('TT - EVENT - Wrapped clicked');
                  navigate(`/giocatore/${id}/wrapped`);
                } else {
                  pushEvent('TT - EVENT - Wrapped dialog opened');
                  setIsWrappedDialogOpen(true);
                }
              }}
            >
              🎾 Wrapped {new Date().getFullYear()}
            </Button>
          </div>
        )}
        <div className='flex flex-row gap-2'>
          <div id='invite'>
            <InviteDialog currentUser={true} />
          </div>
          <div id='modifyProfile'>
            <Button
              variant='outline'
              className='min-w-36 whitespace-nowrap hover:cursor-pointer'
              onClick={() => {
                Analytics.sendEvent({
                  category: 'Profile Interaction',
                  action: 'Modify Profile',
                  label: 'Player Header',
                });
                pushEvent('TT - EVENT - Click to Modify Profile');
                navigate('/profilo/modifica');
              }}
            >
              <GearIcon />
              Modifica Profilo
            </Button>
          </div>
          <ShareHeaderButton url={`/giocatore/${id}`} name={name || 'Giocatore'} type='giocatore' />
        </div>
      </div>
    </div>
  );

  const miniHeader = (
    <div
      className={classNames(
        { 'sticky z-9 md:top-[calc(8rem-1px)]': subMenuSticky },
        { 'top-[calc(7rem+6px)]': subMenuSticky && Capacitor.isNativePlatform() },
        { 'top-20': subMenuSticky && !Capacitor.isNativePlatform() },
        'bg-neutral-neutral-1 flex max-w-full flex-row items-center justify-center gap-2 rounded-t-xl p-1',
      )}
    >
      <div
        className='border-neutral-neutral-11 flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-full border border-solid'
        onClick={navigateBack}
      >
        <CaretLeftIcon className='text-neutral-neutral-11 h-6 w-6' />
      </div>
      <div className='relative'>
        <Avatar
          className='rounded-xl'
          alt='Avatar Giocatore'
          src={photo}
          fallback={<img src='/images/player-avatar.jpg' className='rounded-xl' />}
        />
        {(user?.active_24h || user?.active_1m) && (
          <ActivePopover big={false} color={user?.active_24h ? ActivePopoverColor.GREEN : ActivePopoverColor.YELLOW} />
        )}
      </div>
      <div id='player-name' className='flex min-w-0 items-start gap-2'>
        <div id='nome' className='min-w-0 flex-1'>
          <div className='truncate font-bold'>{name}</div>
        </div>

        <div id='details2' className='flex gap-2 pb-2'>
          <div>
            <AgeCategoryLabel ageCategory={category} />
          </div>
          <div>
            <RankLabel rank={rank} linkTo={true} />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading || !name) {
    return (
      <>
        <div className='bg-neutral-neutral-1 flex flex-col items-start gap-2 rounded-t-xl p-3 md:flex-row md:items-end'>
          <PlayerObjectSkeleton bigHeader={true} />
          <Skeleton className='h-8 w-16' />
        </div>
        {!hideMenu && subMenu}
      </>
    );
  }

  const nameClassName = classNames('text-neutral-neutral-12 text-2xl font-bold tracking-tight leading-8 md:leading-9', {
    'text-neutral-neutral-7': active === false,
  });

  return (
    <>
      <div
        className='bg-neutral-neutral-1 flex flex-col items-start gap-2 overflow-hidden rounded-t-xl p-3 lg:flex-row'
        ref={ref}
      >
        {size.width < 1024 && (
          <div
            className='border-neutral-neutral-11 flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-full border border-solid'
            onClick={navigateBack}
          >
            <CaretLeftIcon className='text-neutral-neutral-11 h-6 w-6' />
          </div>
        )}
        <div id='player' className='flex w-full items-start gap-2 self-stretch p-2'>
          <div className='relative'>
            <Avatar
              alt='Avatar Giocatore'
              className='flex h-24 w-24 rounded-xl'
              src={photo}
              fallback={<img src='/images/player-avatar.jpg' className='rounded-xl' />}
            />
            {(user?.active_24h || user?.active_1m) && (
              <ActivePopover
                big={true}
                color={user?.active_24h ? ActivePopoverColor.GREEN : ActivePopoverColor.YELLOW}
              />
            )}
          </div>
          <div id='player-main' className='flex max-w-full min-w-0 flex-col items-start gap-2 self-stretch p-2'>
            <div id='player-name' className='flex max-w-full min-w-0 items-start gap-2 overflow-hidden'>
              <div id='nome' className='max-w-full min-w-0 overflow-hidden'>
                <div className={nameClassName}>{name}</div>
              </div>
              <div
                id='membership'
                className={`relative ${isUserPremiumClubMember(premium, premium_expiration) ? '' : ''}`}
              >
                {user_id && (
                  <MembershipLabel
                    premiumMember={isUserPremiumMember(premium, premium_expiration)}
                    premiumClubMember={isUserPremiumClubMember(premium, premium_expiration)}
                  />
                )}
              </div>
            </div>
            <div id='player-details' className='flex flex-col flex-wrap items-baseline gap-x-5 md:flex-row'>
              {user?.nickname && (
                <div id='details1' className='flex items-baseline gap-2 pb-2'>
                  <div>Nickname: </div>
                  <div className='truncate text-lg font-medium'>{user.nickname}</div>
                </div>
              )}
              <div id='details2' className='flex gap-2 pb-2'>
                <div>
                  <AgeCategoryLabel ageCategory={category} />
                </div>
                <div>
                  <RankLabel rank={rank} linkTo={true} />
                </div>
              </div>
              {active === false && (
                <div className='flex items-baseline gap-2 pb-2'>
                  <Badge size='3' variant='soft' color='gray' className='text-neutral-neutral-12 text-sm'>
                    Profilo non attivo
                  </Badge>
                </div>
              )}
              <div id='details3' className='flex gap-2'>
                <div id='visite'>{id && <VisitNumber views={views} playerId={id} />}</div>
              </div>
            </div>
          </div>
        </div>
        {isCurrentUser(user_id!) ? currentUserButtons : buttons}
      </div>
      {!inView && miniHeader}
      {!hideMenu && subMenu}
      <ShowWrappedDialog setIsWrappedOpen={setIsWrappedDialogOpen} isWrappedOpen={isWrappedDialogOpen} />
    </>
  );
};
export default PlayerHeader;
